import { resolveWirePublicId } from '../lib/publicId'
import { ApiError, apiRequest } from './client'

export type ReferenceImageItem = {
  publicId: string
  url: string
  bucket: string
  objectKey: string
  /** 后端解析后的 UI 业务文件夹路径；根目录为空串，不允许前端从 objectKey 兜底推导 */
  folderPath: string
  name: string
  sizeBytes: number
  contentType: string
  /** ISO8601 或后端 LocalDateTime 序列化格式 */
  createdAt: string
  prompt?: string
  roundId?: string
  genItemId?: string
  modelLabel?: string
  aspectLabel?: string
  sourceCreatedAt?: string
  /** 后端落库尺寸，用于瀑布流首帧占位 */
  width?: number | null
  height?: number | null
  /** metadata.referenceImageUrls：图生图时使用的参考图 URL 列表 */
  referenceImageUrls?: string[]
}

type ReferenceImageItemWire = ReferenceImageItem & {
  id?: string
  public_id?: string
}

function normalizeReferenceImageItem(item: ReferenceImageItemWire): ReferenceImageItem {
  return {
    ...item,
    publicId: resolveWirePublicId(item, 'publicId', 'public_id', 'id'),
  }
}

export type ReferenceImageListResponse = {
  items: ReferenceImageItem[]
  total: number
  page: number
  size: number
}

export type ReferenceFolderPathsResponse = {
  paths: string[]
}

export function listReferenceImages(page = 1, size = 20, signal?: AbortSignal, folderPath?: string) {
  const q = new URLSearchParams({ page: String(page), size: String(size) })
  if (folderPath !== undefined) {
    q.set('folderPath', folderPath)
  }
  return apiRequest<ReferenceImageListResponse>(`/v1/uploads/reference-records?${q.toString()}`, {
    signal,
  }).then((res) => ({
    ...res,
    items: res.items.map((item) => normalizeReferenceImageItem(item as ReferenceImageItemWire)),
  }))
}

/** 单页上限，与后端 `ReferenceImageCatalogService#list` 的 size 上限（100）对齐 */
const COLLECT_PAGE_SIZE = 100
const COLLECT_MAX_PAGES = 120

/**
 * 分页拉取图库记录（用于目录树与「是否已存入」判断）。
 *
 * @param folderPath 指定业务文件夹路径（如「上传的参考图」）；不传则为图库根目录。
 *   后端按路径精确匹配该层，不含更深层子目录。
 */
export async function collectAllReferenceImageItems(
  signal?: AbortSignal,
  folderPath?: string,
): Promise<ReferenceImageItem[]> {
  const all: ReferenceImageItem[] = []
  let page = 1
  let total = Infinity
  while (all.length < total && page <= COLLECT_MAX_PAGES) {
    if (signal?.aborted) break
    const res = await listReferenceImages(page, COLLECT_PAGE_SIZE, signal, folderPath)
    all.push(...res.items)
    total = res.total
    if (res.items.length === 0) break
    page += 1
  }
  return all
}

export function genItemIdSetFromReferenceItems(items: ReferenceImageItem[]): Set<string> {
  const s = new Set<string>()
  for (const it of items) {
    if (it.genItemId) s.add(it.genItemId)
  }
  return s
}

/** 图库全量缓存：供存入弹窗目录树与「是否已入库」判断 */
export type LibraryCatalogState = {
  items: ReferenceImageItem[]
  genItemIds: Set<string>
  folderPaths: string[]
}

function catalogLoadErrorMessage(reason: unknown, fallback: string): string {
  if (reason instanceof ApiError) return reason.message
  if (reason instanceof Error && reason.message.trim()) return reason.message
  return fallback
}

/**
 * 拉取图库目录树所需数据；目录与列表并行，失败时合并为一条错误（避免双 Toast）。
 */
export async function fetchLibraryCatalogState(signal?: AbortSignal): Promise<LibraryCatalogState> {
  const [itemsResult, foldersResult] = await Promise.allSettled([
    collectAllReferenceImageItems(signal),
    listReferenceFolders(signal),
  ])

  const errors: string[] = []
  let items: ReferenceImageItem[] = []
  if (itemsResult.status === 'fulfilled') {
    items = itemsResult.value
  } else if (!signal?.aborted) {
    errors.push(catalogLoadErrorMessage(itemsResult.reason, '图库图片列表加载失败'))
  }

  let folderPaths: string[] = []
  if (foldersResult.status === 'fulfilled') {
    folderPaths = foldersResult.value.paths ?? []
  } else if (!signal?.aborted) {
    errors.push(catalogLoadErrorMessage(foldersResult.reason, '图库目录加载失败'))
  }

  if (signal?.aborted) {
    return { items: [], genItemIds: new Set(), folderPaths: [] }
  }

  if (errors.length > 0) {
    const qiniuOnly = errors.every((m) => m.includes('七牛'))
    const message = qiniuOnly
      ? '七牛图库拉取失败，请检查存储账号配置与权限'
      : errors.length === 1
        ? errors[0]!
        : errors.join('；')
    throw new ApiError(message, 'UPLOAD_FAILED', 502)
  }

  return {
    items,
    genItemIds: genItemIdSetFromReferenceItems(items),
    folderPaths,
  }
}

export function deleteReferenceImage(publicId: string) {
  const id = encodeURIComponent(publicId)
  return apiRequest<void>(`/v1/uploads/reference-records/${id}`, { method: 'DELETE' })
}

export function listReferenceFolders(signal?: AbortSignal) {
  return apiRequest<ReferenceFolderPathsResponse>(`/v1/uploads/reference-folders`, { signal })
}

export function createReferenceFolder(path: string) {
  return apiRequest<void>(`/v1/uploads/reference-folders`, {
    method: 'POST',
    body: JSON.stringify({ path }),
  })
}

export function deleteReferenceFolder(path: string) {
  const query = new URLSearchParams({ path: path.trim() })
  return apiRequest<void>(`/v1/uploads/reference-folders?${query.toString()}`, { method: 'DELETE' })
}

/** 重命名文件夹（仅改末段；子目录与图片一并迁移） */
export function renameReferenceFolder(path: string, newName: string) {
  return apiRequest<void>(`/v1/uploads/reference-folders/rename`, {
    method: 'PUT',
    body: JSON.stringify({ path: path.trim(), newName: newName.trim() }),
  })
}

/** 整体移动文件夹到新父目录（保留 leaf 名；子目录与图片一并迁移） */
export function moveReferenceFolder(path: string, targetPath: string) {
  return apiRequest<void>(`/v1/uploads/reference-folders/move`, {
    method: 'PUT',
    body: JSON.stringify({ path: path.trim(), targetPath: targetPath.trim() }),
  })
}

export function moveReferenceImage(publicId: string, folderPath: string) {
  const id = encodeURIComponent(publicId)
  return apiRequest<ReferenceImageItem>(`/v1/uploads/reference-records/${id}/move`, {
    method: 'PUT',
    body: JSON.stringify({ folderPath }),
  })
}

export function renameReferenceImage(publicId: string, name: string) {
  const id = encodeURIComponent(publicId)
  return apiRequest<ReferenceImageItem>(`/v1/uploads/reference-records/${id}/rename`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  })
}

/** 将全部图片收起到前缀根目录，并移除所有文件夹占位（含日期类子目录的空壳） */
export type FlattenFoldersResult = {
  referencesMovedToRoot: number
  folderPlaceholdersRemoved: number
}

export function flattenReferenceFoldersToRoot() {
  return apiRequest<FlattenFoldersResult>(`/v1/uploads/reference-records/flatten-to-root`, {
    method: 'POST',
  })
}

export type SyncCloudIndexResult = {
  scanned: number
  inserted: number
  skipped: number
}

export function syncReferenceCloudIndex() {
  return apiRequest<SyncCloudIndexResult>(`/v1/uploads/reference-records/sync-cloud-index`, {
    method: 'POST',
  })
}

export function refreshReferenceImageUrlByObjectKey(objectKey: string) {
  const path = `/v1/uploads/reference-records/refresh-url`
  const body = JSON.stringify({ objectKey })
  return apiRequest<ReferenceImageItem>(path, {
    method: 'POST',
    body,
  }).catch((e: unknown) => {
    if (e instanceof ApiError && e.status === 405) {
      return apiRequest<ReferenceImageItem>(path, {
        method: 'PUT',
        body,
      })
    }
    throw e
  })
}
